import { Banner, TextLink } from 'slab'
import type { EnrollmentEvent } from 'types'
import { conditionallyLocalizeDate, getDateString, getUTCToday } from 'utils'

import { ProfileMenu } from './components/ProfileMenu'

function getEnrollmentEventErrorMessageBasedOnStartDate (startDate: string) {
  // Calculate difference in days between start date and today
  const startDateObj = new Date(startDate)
  const todayObj = getUTCToday()
  const diffTime = todayObj.getTime() - startDateObj.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays <= 60) {
    // Calculate date 60 days after enrollment start date
    const deadlineDate = new Date(startDateObj)
    deadlineDate.setDate(deadlineDate.getDate() + 60)

    return (
      <p>
        If you'd still like to sign up, you can reach out to{' '}
        <TextLink href='mailto:support@thatch.com' target='_blank'>
          support@thatch.com
        </TextLink>{' '}
        by {conditionallyLocalizeDate(deadlineDate)} to reopen your window.
      </p>
    )
  } else {
    return (
      <p>
        If you've had a{' '}
        <TextLink
          href='https://thatch.com/qualifying-life-event'
          target='_blank'
        >
          qualifying life event
        </TextLink>{' '}
        (QLE) and want to sign up now, please reach out to{' '}
        <TextLink href='mailto:support@thatch.com' target='_blank'>
          support@thatch.com
        </TextLink>{' '}
        to get a new enrollment window started.
      </p>
    )
  }
}

export default function EnrollmentEventErrors ({
  enrollmentEvents,
  openEnrollmentNotActive,
  ichraInviteWithSamePlanYearOnDecember,
  medicareEnrollmentBlocked,
}: {
  enrollmentEvents: Array<EnrollmentEvent> | undefined;
  openEnrollmentNotActive: boolean;
  ichraInviteWithSamePlanYearOnDecember: boolean;
  /** Blocked from QLE/SEP because member already has confirmed Medicare for plan year */
  medicareEnrollmentBlocked?: boolean;
}) {
  let message = null
  let bannerTitle = 'Enrollment is closed'

  if (medicareEnrollmentBlocked) {
    bannerTitle = "Your Medicare coverage can't be changed on Thatch"
    message = (
      <div>
        <p>
          If you need to make a different change, like getting coverage for a
          dependent or correcting your expected costs, you can reach out to{' '}
          <TextLink href='mailto:support@thatch.com' target='_blank'>
            support@thatch.com
          </TextLink>{' '}
          for help.
        </p>
      </div>
    )
  } else if (ichraInviteWithSamePlanYearOnDecember) {
    bannerTitle = "Hmm… something's not right"

    message = (
      <div>
        Please reach out to{' '}
        <TextLink href='mailto:support@thatch.com' target='_blank'>
          support@thatch.com
        </TextLink>{' '}
        for help.
      </div>
    )
  } else if (openEnrollmentNotActive) {
    // We will get into this state if renewals feature flag is off and but the enrollment_event window is open
    // We will show a message to the user that they are early and they can come back when the open enrollment window starts.
    bannerTitle = "You're early!"
    const openEnrollmentEvent = enrollmentEvents?.filter(
      (e) =>
        e.enrollment_type === 'open_enrollment' &&
        (e.status === 'active' || e.status === 'in_progress')
    )
    const notYetStarted = openEnrollmentEvent?.[0]
    bannerTitle = "You're early!"

    if (notYetStarted) {
      message = (
        <div>
          <p>
            You can start enrolling on{' '}
            {conditionallyLocalizeDate(
              new Date(notYetStarted.enrollment_window_start_date)
            )}
            . Come back and see us then!
          </p>
        </div>
      )
    } else {
      // This should not happen at this point, but doing a safe fallback.
      message = (
        <div>
          <p>
            You can start enrolling when the open enrollment period starts for
            your company. Come back and see us then!
          </p>
        </div>
      )
    }
  } else if (!enrollmentEvents || !enrollmentEvents.length) {
    // If there are no enrollments at all, then we need to show the following message:
    message = (
      <div>
        <p>
          Your employer needs to open an enrollment window for you to sign up —
          please reach out to your company's HR team for assistance.
        </p>
      </div>
    )
  } else {
    // active enrollments, window not open yet -- member has to return when the window is open
    const activeUnstartedEnrollments = enrollmentEvents.filter(
      (e) =>
        e.status === 'active' &&
        e.enrollment_window_start_date > getDateString(getUTCToday())
    )

    // If there are any active or in progress enrollments that have expired, we need to show a message
    const expiredEnrollments = enrollmentEvents.filter(
      (e) =>
        (e.status === 'active' || e.status === 'in_progress') &&
        e.enrollment_window_end_date < getDateString(getUTCToday()) // TODO(ashley): Change to PST. See MBR-192
    )

    // Get the most recent enrollment event (it is sorted in ascending order from the backend)
    const mostRecent = enrollmentEvents[enrollmentEvents.length - 1]

    if (activeUnstartedEnrollments.length) {
      const notYetStarted = activeUnstartedEnrollments[0] // sorted in ascending order, grab first
      bannerTitle = "You're early!"
      message = (
        <div>
          <p>
            You can start enrolling on{' '}
            {conditionallyLocalizeDate(
              new Date(notYetStarted.enrollment_window_start_date)
            )}
            . Come back and see us then!
          </p>
        </div>
      )
    } else if (expiredEnrollments.length || mostRecent.status === 'inactive') {
      // Handle both expired and inactive enrollment windows
      message = (
        <div>
          <p>
            Your enrollment window closed on{' '}
            {conditionallyLocalizeDate(
              new Date(mostRecent.enrollment_window_end_date)
            )}
            .{' '}
          </p>
          <br />
          {getEnrollmentEventErrorMessageBasedOnStartDate(
            mostRecent.enrollment_window_start_date
          )}
        </div>
      )
    }
  }

  if (message === null) {
    // there's a scenario we don't expect
    bannerTitle = "Hmm… something's not right"

    message = (
      <div>
        Please reach out to{' '}
        <TextLink href='mailto:support@thatch.com' target='_blank'>
          support@thatch.com
        </TextLink>{' '}
        for help.
      </div>
    )
  }

  return (
    <div className='h-screen items-center flex flex-col'>
      <div className='flex flex-col max-w-[600px]'>
        <div className='py-10'>
          <ProfileMenu withoutContext />
        </div>
        <main className='flex-1'>
          <Banner title={bannerTitle} type='warning'>
            {message}
          </Banner>
        </main>
      </div>
    </div>
  )
}
